import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Copy, Trash } from "lucide-react";
import { toast } from "react-toastify";
import { useSubMenu } from "../../../../../empreenderUI/SubMenuContext";
import ScrollArea from "../../../../../empreenderUI/components/ScrollArea";
import Heading from "../../../../../empreenderUI/ui/Heading/Heading";
import SaveControls from "../../../../../empreenderUI/components/Slots/Buttons";
import Input from "../../../../../empreenderUI/ui/Input";
import Slider from "../../../../../empreenderUI/ui/Slider/Slider";
import Switch from "../../../../../empreenderUI/ui/Switch";
import ColorPicker from "../../../../../empreenderUI/ui/ColorPicker/ColorPicker";
import { Button } from "../../../../../empreenderUI/ui/Button";
import PositionPicker from "../../../../../empreenderUI/ui/positionPicker/PositionPicker";
import { ActionBar } from "../../../../../empreenderUI/ui/ActionBar";
import { useBlockNavigation } from "../../../../../empreenderUI/ui/NavigationBlocker";
import { translations } from "../../../../../utils/language";

const PICKER_TO_WATERMARK = {
	"top-left": "TopLeft",
	"top-center": "TopCenter",
	"top-right": "TopRight",
	"middle-left": "MiddleLeft",
	center: "MiddleCenter",
	"middle-right": "MiddleRight",
	"bottom-left": "BottomLeft",
	"bottom-center": "BottomCenter",
	"bottom-right": "BottomRight",
};

const WATERMARK_TO_PICKER = {
	TopLeft: "top-left",
	TopCenter: "top-center",
	TopRight: "top-right",
	MiddleLeft: "middle-left",
	MiddleCenter: "center",
	MiddleRight: "middle-right",
	BottomLeft: "bottom-left",
	BottomCenter: "bottom-center",
	BottomRight: "bottom-right",
};

const WATERMARK_TAGS = ["{{nome}}", "{{email}}", "{{numero_pedido}}"];

const parseWatermarkConfig = (raw) => {
	if (typeof raw === "string" && raw) {
		try {
			return JSON.parse(raw);
		} catch {
			return {};
		}
	}
	if (raw && typeof raw === "object") return raw;
	return {};
};

const normalizeWatermarkConfig = (raw) => {
	const parsed = parseWatermarkConfig(raw);
	const opacity = Number(parsed.watermark_opacity ?? 50);
	return JSON.stringify({
		watermark_header_text: parsed.watermark_header_text || "",
		watermark_footer_text: parsed.watermark_footer_text || "",
		watermark_opacity: Number.isNaN(opacity) ? 50 : opacity,
		watermark_text_color: parsed.watermark_text_color || "#000000",
		watermark_status: !!parsed.watermark_status,
		watermark_image_position: parsed.watermark_image_position || "MiddleCenter",
	});
};

const WatermarkTags = ({ language }) => {
	const copyTag = (tag) => {
		navigator.clipboard.writeText(tag);
		toast.success(translations["thema-codigo-copiado"]?.[language.data] || "Copiado.");
	};

	return (
		<div className="flex flex-wrap gap-1.5 pt-0.5">
			{WATERMARK_TAGS.map((tag) => (
				<button
					key={tag}
					type="button"
					onClick={() => copyTag(tag)}
					title={`${translations["common-copy"][language.data]} ${tag}`}
					className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-[11px] text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-100"
				>
					{tag}
					<Copy size={12} className="shrink-0 text-gray-400" />
				</button>
			))}
		</div>
	);
};

const MarcaAguaContent = () => {
	const { toggle } = useSubMenu();
	const history = useHistory();
	const dispatch = useDispatch();
	const controlConfig = useSelector((state) => state.reducerControlConfig);
	const language = useSelector((state) => state.reducerLanguage);
	const config = useSelector((state) => state.reducerTemplate);
	const saved = config?.data?.[0];

	const parsedConfig = parseWatermarkConfig(controlConfig?.watermark_config);

	const isDirty = useMemo(() => {
		if (!saved) return false;
		if (normalizeWatermarkConfig(controlConfig.watermark_config) !== normalizeWatermarkConfig(saved.watermark_config)) {
			return true;
		}
		if (controlConfig.watermark_image_file) return true;
		if ((controlConfig.watermark_image_url ?? "") !== (saved.watermark_image_url ?? "")) return true;
		return false;
	}, [
		controlConfig.watermark_config,
		controlConfig.watermark_image_file,
		controlConfig.watermark_image_url,
		saved,
	]);

	useBlockNavigation(isDirty);

	const updateConfig = (patch) => {
		const nextCfg = { ...parsedConfig, ...patch };
		dispatch({
			type: "CONFIG_CONTROL_SET",
			payload: { watermark_config: JSON.stringify(nextCfg) },
		});
	};

	const saveConfig = () => {
		const watermark_config = JSON.stringify({
			watermark_header_text: parsedConfig.watermark_header_text || "",
			watermark_footer_text: parsedConfig.watermark_footer_text || "",
			watermark_opacity: parsedConfig.watermark_opacity ?? 50,
			watermark_text_color: parsedConfig.watermark_text_color || "#000000",
			watermark_status: parsedConfig.watermark_status || false,
			watermark_image_position: parsedConfig.watermark_image_position || "MiddleCenter",
		});

		const payload = { watermark_config };
		if (controlConfig.watermark_image_file) {
			payload.watermark_image = controlConfig.watermark_image_file;
		}
		if (!controlConfig.watermark_image_url) {
			payload.watermark_image_url = "";
		}

		dispatch({ type: "SAGA_UPDATE_WATERMARK", payload });
	};

	const discardChanges = () => {
		dispatch({ type: "SAGA_GET_TEMPLATES" });
	};

	const getDefaultConfig = () => {
		const watermark_config = JSON.stringify({
			watermark_header_text: "",
			watermark_footer_text: "",
			watermark_opacity: 50,
			watermark_text_color: "#000000",
			watermark_status: false,
			watermark_image_position: "MiddleCenter",
		});
		dispatch({
			type: "SAGA_UPDATE_WATERMARK",
			payload: { watermark_config, watermark_image_url: "" },
		});
	};

	const changeStatusActive = (checked) => {
		const watermark_config = JSON.stringify({
			...parsedConfig,
			watermark_status: checked,
		});
		dispatch({ type: "SAGA_UPDATE_WATERMARK", payload: { watermark_config } });
	};

	const handleImageUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		const img = new Image();
		const reader = new FileReader();
		reader.onload = (ev) => {
			img.onload = () => {
				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d");
				const targetW = 100;
				const targetH = 100;
				canvas.width = targetW;
				canvas.height = targetH;
				ctx.clearRect(0, 0, targetW, targetH);
				const ratio = Math.min(targetW / img.width, targetH / img.height);
				const drawW = Math.round(img.width * ratio);
				const drawH = Math.round(img.height * ratio);
				const dx = Math.round((targetW - drawW) / 2);
				const dy = Math.round((targetH - drawH) / 2);
				ctx.drawImage(img, dx, dy, drawW, drawH);
				const dataUrl = canvas.toDataURL("image/png");
				dispatch({
					type: "CONFIG_CONTROL_SET",
					payload: { watermark_image: dataUrl },
				});
				canvas.toBlob((blob) => {
					if (blob) {
						const resizedFile = new File(
							[blob],
							`${file.name.replace(/\.[^.]+$/, "")}-100x100.png`,
							{ type: "image/png" }
						);
						dispatch({
							type: "CONFIG_CONTROL_SET",
							payload: { watermark_image_file: resizedFile },
						});
					} else {
						dispatch({
							type: "CONFIG_CONTROL_SET",
							payload: { watermark_image_file: file },
						});
					}
				}, "image/png");
			};
			img.src = ev.target.result;
		};
		reader.readAsDataURL(file);
	};

	const positionValue = parsedConfig?.watermark_image_position ?? "MiddleLeft";

	return (
		<ScrollArea>
			<Heading
				header={translations["marca-agua"]?.[language.data] || "Marca d'água"}
				onClick={() => {
					history.push("/personalizar");
					toggle("personalizar");
				}}
			/>

			<div className="flex flex-col gap-5 px-6 py-4">
				<div className="flex flex-row items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2.5">
					<div className="flex flex-col gap-0.5 min-w-0">
						<p className="text-sm font-medium text-gray-900">
							{translations["marca-agua"][language.data]}
						</p>
						<p className="text-xs text-gray-500">
							{translations["marca-agua-ativo"][language.data]}
						</p>
					</div>
					<Switch
						checked={!!parsedConfig.watermark_status}
						onCheckedChange={changeStatusActive}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-gray-700">
						{translations["marca-agua-cor-texto"][language.data]}
					</label>
					<ColorPicker
						side="right"
						sideOffset={12}
						align="start"
						color={parsedConfig.watermark_text_color || "#000000"}
						onChange={(cor) => updateConfig({ watermark_text_color: cor })}
						placeholder={translations["marca-agua-cor-texto"][language.data]}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-gray-700">
						{translations["marca-agua-texto-cabecalho"][language.data]}
					</label>
					<Input
						type="text"
						className="!border !border-solid !border-gray-200 !bg-white"
						value={parsedConfig.watermark_header_text || ""}
						onChange={(e) => updateConfig({ watermark_header_text: e.target.value })}
						placeholder={translations["marca-agua-texto-cabecalho"][language.data]}
					/>
					<WatermarkTags language={language} />
				</div>

				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-gray-700">
						{translations["marca-agua-texto-rodape"][language.data]}
					</label>
					<Input
						type="text"
						className="!border !border-solid !border-gray-200 !bg-white"
						value={parsedConfig.watermark_footer_text || ""}
						onChange={(e) => updateConfig({ watermark_footer_text: e.target.value })}
						placeholder={translations["marca-agua-texto-rodape"][language.data]}
					/>
					<WatermarkTags language={language} />
				</div>

				<div className="flex flex-col gap-2">
					<label className="text-xs font-medium text-gray-700">
						{translations["marca-agua-selecionar-imagem"][language.data]}
					</label>
					<input
						id="watermark-file-input-content"
						type="file"
						accept="image/*"
						onChange={handleImageUpload}
						className="hidden"
					/>
					<Button
						type="button"
						className="w-full"
						onClick={() => document.getElementById("watermark-file-input-content")?.click()}
					>
						{translations["marca-agua-selecionar-imagem"][language.data]}
					</Button>

					{(controlConfig.watermark_image || controlConfig.watermark_image_url) && (
						<div className="relative mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
							<div className="mb-3 flex items-center justify-between">
								<label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
									<span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
									{translations["marca-agua-preview-imagem"][language.data]}
								</label>
								<Button
									type="button"
									variant="outline"
									size="icon"
									className="!size-7 border-red-200 bg-red-100 text-red-600 hover:bg-red-200"
									onClick={() => {
										if (controlConfig.watermark_image) {
											dispatch({
												type: "CONFIG_CONTROL_SET",
												payload: { watermark_image: "", watermark_image_file: null },
											});
										} else {
											dispatch({
												type: "CONFIG_CONTROL_SET",
												payload: { watermark_image_url: "" },
											});
										}
									}}
									title={
										translations[
											controlConfig.watermark_image
												? "marca-agua-remover-local"
												: "marca-agua-remover-api"
										]?.[language.data]
									}
								>
									<Trash size={14} />
								</Button>
							</div>
							<div className="mx-auto flex h-[120px] w-[120px] items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50">
								<img
									src={controlConfig.watermark_image || controlConfig.watermark_image_url}
									alt={translations["marca-agua-preview-alt"][language.data]}
									className="h-full w-full rounded-lg object-contain"
								/>
							</div>
							<div className="mt-2 text-center">
								<span className="inline-block rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-500">
									100x100px
								</span>
							</div>
						</div>
					)}
				</div>

				<div className="flex flex-col gap-2">
					<label className="text-xs font-medium text-gray-700">
						{translations["marca-agua-posicao-imagem"][language.data]}
					</label>
					<PositionPicker
						value={WATERMARK_TO_PICKER[positionValue] || "center"}
						defaultValue={WATERMARK_TO_PICKER[positionValue] || "center"}
						onSelect={(pos) =>
							updateConfig({
								watermark_image_position: PICKER_TO_WATERMARK[pos] || "MiddleCenter",
							})
						}
					/>
				</div>

				<div className="flex flex-col gap-3 pb-2">
					<label className="text-xs font-medium text-gray-700">
						{translations["marca-agua-opacidade-imagem"]?.[language.data] || "Opacidade da Imagem (%)"}
					</label>
					<div className="flex items-center gap-3 w-full py-2">
						<div className="flex-1 min-w-0 [&_.slider-root-empreender]:h-5 [&_.slider-root-empreender]:items-center [&_[data-slot=slider-thumb]]:translate-y-0">
							<Slider
								min={0}
								max={100}
								step={1}
								value={parsedConfig.watermark_opacity ?? 50}
								onChange={(value) => {
									const num = Array.isArray(value) ? value[0] : value;
									const v = Number(num);
									const clamped = isNaN(v) ? 0 : Math.max(0, Math.min(100, v));
									updateConfig({ watermark_opacity: clamped });
								}}
							/>
						</div>
						<span className="min-w-[42px] shrink-0 text-right text-xs text-gray-500 tabular-nums">
							{`${parsedConfig.watermark_opacity ?? 50}%`}
						</span>
					</div>
				</div>
			</div>

			<SaveControls
				onCancel={getDefaultConfig}
				cancelText={translations["thema-restaurar"]?.[language.data]}
			/>

			<ActionBar
				open={isDirty}
				textos={{
					mainText: translations["thema-cuidado-salvar"]?.[language.data],
				}}
				onSave={(proceed) => {
					saveConfig();
					proceed();
				}}
				onCancel={discardChanges}
			/>
		</ScrollArea>
	);
};

export default MarcaAguaContent;
